import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Mail, Lock, User, AtSign, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/* --------------------------- small primitives --------------------------- */

function Field({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
      <input
        {...props}
        className="w-full h-12 rounded-full bg-white/[0.04] border border-white/10 pl-11 pr-4 text-sm text-white placeholder-white/40 outline-none focus:border-white/40 focus:bg-white/[0.06] transition-colors"
      />
    </div>
  );
}

function PrimaryButton({ children, loading, ...props }) {
  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      className="group relative w-full h-12 rounded-full font-semibold text-sm text-white overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <span className="absolute inset-0 bg-gradient-to-r from-[#ef4444] via-[#c026d3] to-[#a855f7]" />
      <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-[#a855f7] via-[#c026d3] to-[#ef4444]" />
      <span className="relative flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
      </span>
    </button>
  );
}

/* -------------------- 6-digit code input (auto advance) -------------------- */

function CodeInput({ value, onChange, disabled }) {
  const refs = useRef([]);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");
  const setDigit = (i, d) => {
    const cleaned = d.replace(/\D/g, "").slice(0, 1);
    const next = value.padEnd(6, " ").slice(0, 6).split("");
    next[i] = cleaned || " ";
    const joined = next.join("").replace(/\s+$/, "");
    onChange(joined);
    if (cleaned && i < 5) refs.current[i + 1]?.focus();
  };
  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i].trim() && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };
  const onPaste = (e) => {
    const t = e.clipboardData?.getData("text")?.replace(/\D/g, "").slice(0, 6);
    if (t) { e.preventDefault(); onChange(t); refs.current[Math.min(5, t.length - 1)]?.focus(); }
  };
  return (
    <div className="flex gap-2 justify-between" onPaste={onPaste} data-testid="code-input">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={d.trim()}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKey(i, e)}
          className="w-11 h-14 sm:w-12 rounded-xl text-center text-xl font-serif font-bold bg-white/[0.04] border border-white/10 text-white outline-none focus:border-white/50 focus:bg-white/[0.08] transition-colors"
          data-testid={`code-digit-${i}`}
        />
      ))}
    </div>
  );
}

/* ------------------------------- Auth page ------------------------------- */

export default function Auth() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // step: "email" | "signin" | "signup" | "verify"
  const [step, setStep] = useState("email");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);

  // Resend cool-down
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const errMsg = (e, fallback = "Something went wrong") => {
    const d = e?.response?.data?.detail;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
    return (typeof d === "string" && d) || e?.message || fallback;
  };

  /* ---------------------- step 1 — check email ---------------------- */
  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/email-check", { email });
      if (r.data.exists) {
        setStep("signin");
      } else {
        // Suggest a username slug from the email local-part
        const slug = email.split("@")[0].replace(/[^a-z0-9_]/gi, "").toLowerCase().slice(0, 20);
        setUsername((prev) => prev || slug);
        setStep("signup");
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  /* ------------------------- step 2 — sign in ------------------------- */
  const submitSignIn = async (e) => {
    e?.preventDefault?.();
    if (!password) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/login-email", { email, password });
      login(r.data.token, r.data.user);
      toast.success(`Welcome back, ${r.data.user.full_name?.split(" ")[0] || "friend"}`);
      navigate(r.data.user.role === "admin" ? "/admin" : "/home");
    } catch (e) { toast.error(errMsg(e, "Incorrect password")); }
    finally { setBusy(false); }
  };

  /* ---------------------- step 2b — sign up start ---------------------- */
  const submitSignUp = async (e) => {
    e?.preventDefault?.();
    if (fullName.trim().length < 2) { toast.error("Enter your full name"); return; }
    if (!/^[a-z0-9_]{3,20}$/.test(username)) { toast.error("Username: 3-20 chars, lowercase / digits / _"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/signup-request", { email, full_name: fullName, username, password });
      setPendingId(r.data.pending_id);
      setCode("");
      setStep("verify");
      setResendIn(45);
      if (r.data.dev_code) {
        // SMTP was unavailable — show the code inline so the user isn't blocked
        toast.info(`Dev code: ${r.data.dev_code}`, { duration: 12000 });
      } else {
        toast.success("Verification code sent to your email");
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  /* ---------------------- step 3 — verify code ---------------------- */
  const submitVerify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/signup-verify", { pending_id: pendingId, code });
      login(r.data.token, r.data.user);
      toast.success(`Welcome to Truebate, ${r.data.user.full_name?.split(" ")[0]} 🎉`);
      navigate("/home");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (resendIn > 0 || !pendingId) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/signup-resend", { pending_id: pendingId });
      setResendIn(45);
      if (r.data.dev_code) toast.info(`Dev code: ${r.data.dev_code}`, { duration: 12000 });
      else toast.success("Code re-sent");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const back = () => {
    if (step === "signin" || step === "signup") { setStep("email"); setPassword(""); }
    else if (step === "verify") { setStep("signup"); setCode(""); setPendingId(null); }
  };

  /* -------------------------------- render -------------------------------- */

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white relative overflow-hidden flex items-center justify-center px-4 py-16">
      {/* Ambient gradients */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-20 w-[560px] h-[560px] rounded-full bg-[#ef4444]/25 blur-[140px]" />
        <div className="absolute -right-40 bottom-10 w-[560px] h-[560px] rounded-full bg-[#a855f7]/25 blur-[140px]" />
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-[#ef4444]/10 via-[#c026d3]/10 to-[#a855f7]/10 blur-[100px]" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link
            to="/"
            className="inline-block font-serif text-3xl tracking-tight bg-gradient-to-r from-[#ef4444] via-[#c026d3] to-[#a855f7] bg-clip-text text-transparent"
            data-testid="auth-brand"
          >
            Truebate<span className="text-white/40">.</span>
          </Link>
          <p className="mt-2 text-xs font-mono uppercase tracking-[0.24em] text-white/40">
            Where every opinion gets a voice
          </p>
        </div>

        {/* Card */}
        <div className="relative rounded-[28px] p-[1px] bg-gradient-to-br from-[#ef4444]/50 via-[#c026d3]/50 to-[#a855f7]/50 shadow-2xl shadow-[#a855f7]/10">
          <div className="rounded-[27px] bg-black/70 backdrop-blur-xl p-8 sm:p-10">
            {/* Step header */}
            <div className="mb-6">
              {step !== "email" && (
                <button
                  onClick={back}
                  className="mb-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors"
                  data-testid="auth-back-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <h2 className="font-serif text-3xl leading-tight">
                {step === "email" && "Sign in or create an account"}
                {step === "signin" && "Welcome back."}
                {step === "signup" && "One moment — you're new here."}
                {step === "verify" && "Check your inbox."}
              </h2>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                {step === "email" && "Enter your email to get started. We'll take it from there."}
                {step === "signin" && (<>Signing in as <span className="text-white/90">{email}</span></>)}
                {step === "signup" && (<>Setting up your <span className="text-white/90">{email}</span> account.</>)}
                {step === "verify" && (<>We sent a 6-digit code to <span className="text-white/90">{email}</span>. It expires in 10 minutes.</>)}
              </p>
            </div>

            <AnimatePresence mode="wait">
              {/* Step 1 — email */}
              {step === "email" && (
                <motion.form
                  key="email" onSubmit={submitEmail}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }} className="space-y-4"
                >
                  <Field
                    icon={Mail} type="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    autoFocus autoComplete="email" data-testid="auth-email-input"
                  />
                  <PrimaryButton type="submit" loading={busy} data-testid="auth-email-continue">
                    Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </PrimaryButton>
                  <div className="pt-4 flex items-center gap-2 text-[11px] text-white/40 justify-center">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>Aadhaar-verified only. No trolls, no bots.</span>
                  </div>
                </motion.form>
              )}

              {/* Step 2a — sign in */}
              {step === "signin" && (
                <motion.form
                  key="signin" onSubmit={submitSignIn}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }} className="space-y-4"
                >
                  <Field
                    icon={Lock} type="password" placeholder="Your password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    autoFocus autoComplete="current-password" data-testid="auth-password-input"
                  />
                  <PrimaryButton type="submit" loading={busy} data-testid="auth-signin-submit">
                    Sign in <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </PrimaryButton>
                </motion.form>
              )}

              {/* Step 2b — sign up */}
              {step === "signup" && (
                <motion.form
                  key="signup" onSubmit={submitSignUp}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }} className="space-y-4"
                >
                  <Field
                    icon={User} type="text" placeholder="Full name"
                    value={fullName} onChange={(e) => setFullName(e.target.value)}
                    autoFocus autoComplete="name" data-testid="auth-fullname-input"
                  />
                  <Field
                    icon={AtSign} type="text" placeholder="username" spellCheck={false}
                    value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-z0-9_]/gi, "").toLowerCase())}
                    autoComplete="username" data-testid="auth-username-input"
                  />
                  <Field
                    icon={Lock} type="password" placeholder="Password (min 6 chars)"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password" data-testid="auth-newpassword-input"
                  />
                  <PrimaryButton type="submit" loading={busy} data-testid="auth-signup-submit">
                    Send verification code <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </PrimaryButton>
                  <p className="text-[11px] text-white/40 text-center pt-2">
                    By continuing you agree to our terms and privacy policy.
                  </p>
                </motion.form>
              )}

              {/* Step 3 — verify code */}
              {step === "verify" && (
                <motion.form
                  key="verify" onSubmit={submitVerify}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }} className="space-y-5"
                >
                  <CodeInput value={code} onChange={setCode} disabled={busy} />
                  <PrimaryButton type="submit" loading={busy} data-testid="auth-verify-submit">
                    <KeyRound className="w-4 h-4" /> Verify &amp; create account
                  </PrimaryButton>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-white/40">
                      Didn&apos;t get it? {resendIn > 0 ? `Resend in ${resendIn}s` : ""}
                    </span>
                    <button
                      type="button"
                      onClick={resend}
                      disabled={resendIn > 0 || busy}
                      className="text-white/80 hover:text-white disabled:text-white/30 disabled:cursor-not-allowed underline underline-offset-2"
                      data-testid="auth-resend-btn"
                    >
                      Resend code
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-white/40">
          Back to <Link to="/" className="text-white/80 hover:text-white underline underline-offset-2" data-testid="auth-back-home">home</Link>
        </div>
      </div>
    </div>
  );
}
